import { useEffect, useRef } from "react";

const SCREEN_ATTR = "data-calendar-screen";

/** Spread on every calendar screen's root, so the calendar's keys act only when focus is in the calendar. */
export const calendarScreenProps = { [SCREEN_ATTR]: "" };

/**
 * Whether a key pressed on `target` is for the calendar: not while typing in a
 * field, and only when focus is in the calendar or nowhere in particular (the
 * page itself), so other parts of the pane keep their keys. With focus
 * nowhere, the calendar must also be on view: Settings hides it without
 * unmounting it, and the AI notice sheet makes it inert.
 */
function isCalendarKeyTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable || target.closest("input, textarea, select, [role='listbox']")) return false;
  if (target.closest(`[${SCREEN_ATTR}]`)) return true;
  if (target !== document.body) return false;
  const screen = document.querySelector(`[${SCREEN_ATTR}]`);
  return screen !== null && screen.getClientRects().length > 0 && !screen.closest("[inert]");
}

/**
 * The mockup's keyboard shortcuts: `onKey` gets each key pressed for the
 * calendar (see isCalendarKeyTarget) that nothing else has handled. Each
 * screen handles the keys for its own buttons.
 */
export function useCalendarKeys(onKey: (e: KeyboardEvent) => void): void {
  const onKeyRef = useRef(onKey);
  onKeyRef.current = onKey;
  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing || !isCalendarKeyTarget(e.target)) return;
      onKeyRef.current(e);
    };
    document.addEventListener("keydown", listener);
    return () => document.removeEventListener("keydown", listener);
  }, []);
}
